import html2plaintext from 'html2plaintext';

const TOKENIZER_REGEX = /\s+/;

// Keep consistent with the frontend estimate.
const AVERAGE_READING_SPEED_WPM = 238; // https://scholarwithin.com/average-reading-speed#average-reading-speed-by-age-and-grade

export type ReadingTimeEstimate = {
  wordCount: number;
  minutesRaw: number;
  minutes: number;
  wpm: number;
};

export const documentToPlainTextString = (html: string): string => {
  try {
    return html2plaintext(html || '');
  } catch {
    // Defensive fallback strip tags if something unexpected happens.
    // Not needed but its a cheap safety net
    return String(html || '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ');
  }
};

const wordCountFromText = (text: string): number => {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(TOKENIZER_REGEX).filter(Boolean).length;
};

/**
 * Returns an estimated reading time from an HTML string
 * - wordCount: whitespace-tokenized words from HTML -> plain text
 * - minutesRaw: wordCount / wpm
 * - minutes: rounded minutes (nearest whole minute)
 */
export const readingTimeForArticle = (
  html: string,
  wpm: number = AVERAGE_READING_SPEED_WPM,
): ReadingTimeEstimate => {
  const plainText = documentToPlainTextString(html || '');
  const wordCount = wordCountFromText(plainText);
  const minutesRaw = wpm > 0 ? wordCount / wpm : 0;
  // If there's any readable text at all, show at least 1 minute.
  const minutes = wordCount > 0 ? Math.max(1, Math.round(minutesRaw)) : 0;
  return { wordCount, minutesRaw, minutes, wpm };
};
